import { useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getCategoriesForSettings, saveCategoriesForSettings } from "services/Categories.services";
import { getCompanySettings, updateCompanySettings } from "services/CompanySettings.services";
import { toast } from "sonner";

import useCategoriesForSession from "hooks/useCategoriesForSession";

import type { Category, CategoryRequest, CategorySettingsResponse } from "types/Category.type";

import i18n from "utils/i18n";
import { queryClient } from "utils/queryClient";

export default function useCategories() {
  // ===========================================================================
  // HOOKS
  // =============================================================================
  const { t } = useTranslation();
  const { categoriesSession, isLoadingSession } = useCategoriesForSession();

  // ===========================================================================
  // GET CATEGORIES SETTINGS
  // ===========================================================================
  const {
    data: categoriesSettings,
    isLoading: isLoadingSettings,
    error: errorSettings,
  } = useQuery<CategorySettingsResponse>({
    queryKey: ["categoriesSettings"],
    queryFn: getCategoriesForSettings,
  });

  // ===========================================================================
  // GET COMPANY SETTINGS (for aiEnhancmentLevel)
  // ===========================================================================
  const { data: companySettings, isLoading: isLoadingCompanySettings } = useQuery<any>({
    queryKey: ["companySettings"],
    queryFn: getCompanySettings,
  });

  // ===========================================================================
  // AI ENHANCEMENT LEVEL STATE
  // ===========================================================================
  const [aiEnhancmentLevel, setAiEnhancementLevel] = useState<0 | 1>(0);
  const [initialAiEnhancementLevel, setInitialAiEnhancementLevel] = useState<0 | 1>(0);
  const [isAiEnhancementLevelDirty, setIsAiEnhancementLevelDirty] = useState(false);
  const [aiCategoriesEnabled, setAiCategoriesEnabled] = useState(companySettings?.aiCategoriesEnabled ?? false);
  const [isAiCategoriesEnabledDirty, setIsAiCategoriesEnabledDirty] = useState(false);

  useEffect(() => {
    const level = (companySettings as any)?.aiEnhancmentLevel ?? 0;
    setAiEnhancementLevel(level);
    setInitialAiEnhancementLevel(level);
    setAiCategoriesEnabled(companySettings?.aiCategoriesEnabled ?? false);
  }, [companySettings]);

  // ===========================================================================
  // SAVE CATEGORIES SETTINGS
  // ===========================================================================
  const saveCategoriesSettings = useMutation<Category[], Error, CategoryRequest[]>({
    mutationFn: (data) => saveCategoriesForSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categoriesSettings"] });
    },
  });

  // ===========================================================================
  // SAVE COMPANY SETTINGS (aiEnhancmentLevel)
  // ===========================================================================
  const saveCompanySettingsMutation = useMutation({
    mutationFn: (data: any) => updateCompanySettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companySettings"] });
    },
  });

  // ===========================================================================
  // MEMOIZED VALUES - CATEGORIES
  // ===========================================================================

  const categories = useMemo(() => {
    const settings = categoriesSettings?.resultList || [];
    const translations = categoriesSession?.resultList || [];
    const nameMap: Record<string, { name_bs?: string; name_en?: string }> = {};
    translations.forEach((t) => {
      if (!nameMap[t.code]) nameMap[t.code] = {};
      if (t.languageId === 1) nameMap[t.code].name_bs = t.name;
      if (t.languageId === 2) nameMap[t.code].name_en = t.name;
    });
    return settings.map((cat) => ({
      id: cat.id,
      code: cat.code,
      active: cat.active,
      name_bs: nameMap[cat.code]?.name_bs || "",
      name_en: nameMap[cat.code]?.name_en || "",
      description_bs: translations.find((tr) => tr.code === cat.code && tr.languageId === 1)?.description || "",
      description_en: translations.find((tr) => tr.code === cat.code && tr.languageId === 2)?.description || "",
    }));
  }, [categoriesSettings, categoriesSession]);

  const currentLanguage = i18n.language;

  // ===========================================================================
  // FORM
  // ===========================================================================

  const form = useForm({});

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { isDirty, isSubmitting },
  } = form;

  const formValues = watch();
  const atLeastOneCategorySelected = !aiCategoriesEnabled || categories.some((cat) => formValues[cat.code]);

  // ===========================================================================
  // SUBMIT
  // ===========================================================================
  async function onSubmit(values: Record<string, boolean>) {
    const promises: Promise<any>[] = [];

    if (isDirty) {
      const updatedCategories = categories.map((cat) => ({
        id: cat.id,
        active: values[cat.code],
      }));
      promises.push(saveCategoriesSettings.mutateAsync(updatedCategories));
    }

    promises.push(
      saveCompanySettingsMutation.mutateAsync({
        aiEnhancmentLevel,
        aiCategoriesEnabled,
      }),
    );

    await Promise.all(promises);
    setIsAiEnhancementLevelDirty(false);
    setIsAiCategoriesEnabledDirty(false);

    toast.success(t("profile.categories.updateSuccess"), {
      description: t("profile.categories.updateSuccessDescription"),
    });
  }

  const nameKey = `name_${currentLanguage}` as keyof Category;
  const descriptionKey = `description_${currentLanguage}` as keyof Category;

  // ===========================================================================
  // EFFECTS
  // ===========================================================================
  useEffect(() => {
    const DEFAULT_VALUES = Object.fromEntries(categories.map((cat) => [cat.code, cat.active]));
    reset(DEFAULT_VALUES);
  }, [categories, reset]);

  useEffect(() => {
    setIsAiEnhancementLevelDirty(aiEnhancmentLevel !== initialAiEnhancementLevel);
    setIsAiCategoriesEnabledDirty(aiCategoriesEnabled !== (companySettings?.aiCategoriesEnabled ?? false));
  }, [aiEnhancmentLevel, aiCategoriesEnabled, setIsAiEnhancementLevelDirty, setIsAiCategoriesEnabledDirty]);

  return {
    categoriesSettings,
    isLoadingSession,
    isLoadingSettings: isLoadingSettings || isLoadingCompanySettings,
    errorSettings,
    categories,
    form,
    handleSubmit,
    control,
    setValue,
    isDirty: isDirty || isAiEnhancementLevelDirty || isAiCategoriesEnabledDirty,
    isSubmitting,
    onSubmit,
    nameKey,
    descriptionKey,
    aiEnhancmentLevel,
    setAiEnhancementLevel,
    aiCategoriesEnabled,
    setAiCategoriesEnabled,
    atLeastOneCategorySelected,
  };
}
